'use client';

import React, { useEffect, useRef, useState } from 'react';
import { IconPhone, IconUser } from '@tabler/icons-react';
import { IncomingCall } from '@/lib/firebase/types';
import styles from '@/styles/CallModal.module.css';

interface IncomingCallModalProps {
  call: IncomingCall;
  onAccept: () => void;
  onDecline: () => void;
}

const CALL_TIMEOUT_SECONDS = 30;

export function IncomingCallModal({ call, onAccept, onDecline }: IncomingCallModalProps) {
  const [timeRemaining, setTimeRemaining] = useState(CALL_TIMEOUT_SECONDS);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Play ringtone on mount
  useEffect(() => {
    let audio: HTMLAudioElement | null = null;
    let isPlaying = false;

    try {
      audio = new Audio('/sounds/ringtone.mp3');
      audio.loop = true;
      audioRef.current = audio;

      audio.play()
        .then(() => {
          isPlaying = true;
        })
        .catch(() => {
          // Autoplay was prevented - expected behavior
        });
    } catch {
      // Audio creation failed - ignore
    }

    return () => {
      if (audio && isPlaying) {
        try {
          audio.pause();
          audio.currentTime = 0;
        } catch {
          // Ignore cleanup errors
        }
      }
      audioRef.current = null;
    };
  }, []);

  // Auto-decline after timeout
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onDecline();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onDecline]);

  const handleAccept = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } catch {
      // Ignore audio errors
    }
    onAccept();
  };

  const handleDecline = () => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } catch {
      // Ignore audio errors
    }
    onDecline();
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <span className={styles.title}>Incoming Call</span>
          <span className={styles.timer}>{timeRemaining}s</span>
        </div>

        <div className={styles.userInfo}>
          {call.caller_profile_picture ? (
            <img
              src={call.caller_profile_picture}
              alt={call.caller_name}
              className={styles.avatar}
            />
          ) : (
            <div className={styles.avatarFallback}>
              <IconUser size={36} />
            </div>
          )}
          <p className={styles.userName}>{call.caller_name}</p>
          <p className={styles.statusText}>is calling you</p>
        </div>

        <div className={styles.actions}>
          <button
            className={`${styles.actionButton} ${styles.declineButton}`}
            onClick={handleDecline}
          >
            <IconPhone size={20} style={{ transform: 'rotate(135deg)' }} />
            <span>Decline</span>
          </button>
          <button
            className={`${styles.actionButton} ${styles.acceptButton}`}
            onClick={handleAccept}
          >
            <IconPhone size={20} />
            <span>Accept</span>
          </button>
        </div>
      </div>
    </div>
  );
}
